import React, { useEffect, useState, useCallback, useRef } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAvatarManager } from "@/hooks/useAvatarSync";

interface OptimizedAvatarProps {
  /** URL do avatar do usuário */
  src?: string | null;
  /** Nome completo do usuário para fallback */
  userName?: string | null;
  /** Email do usuário para fallback */
  userEmail?: string | null;
  /** Alt text para acessibilidade */
  alt?: string;
  /** Fallback customizado */
  fallback?: string;
  /** Classes CSS adicionais */
  className?: string;
  /** Tamanho do avatar */
  size?: "sm" | "md" | "lg" | "xl";
  /** Força refresh quando true */
  forceRefresh?: boolean;
  /** Callback quando avatar é clicado */
  onClick?: () => void;
  /** Se deve mostrar indicador de carregamento */
  showLoading?: boolean;
}

const sizeClasses = {
  sm: "w-6 h-6",
  md: "w-8 h-8",
  lg: "w-10 h-10",
  xl: "w-12 h-12"
};

const fallbackSizeClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
  xl: "text-lg"
};

/**
 * Componente OptimizedAvatar com cache-busting automático e sincronização global
 *
 * Características principais:
 * - Cache-busting automático com timestamp dinâmico
 * - Key pattern para forçar re-render completo quando necessário
 * - Tratamento de erros no carregamento da imagem
 * - Fallback para iniciais quando imagem falha
 * - Sincronização automática com estado global via Zustand
 */
export function OptimizedAvatar({
  src,
  userName,
  userEmail,
  alt = "Avatar do usuário",
  fallback,
  className = "",
  size = "md",
  forceRefresh = false,
  onClick,
  showLoading = true
}: OptimizedAvatarProps) {
  // Estado global para sincronização de avatar
  // IMPORTANTE: Este hook deve sempre ser chamado na mesma ordem
  const avatarManager = useAvatarManager();
  const {
    avatarUrl: globalAvatarUrl,
    initials: globalInitials,
    avatarKey,
    isLoading: globalLoading,
    error: globalError,
    updateAvatar,
    setLoading,
    setError,
  } = avatarManager;

  // Estado local para tratamento de imagem
  const [imageKey, setImageKey] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageAttempted, setImageAttempted] = useState(false);

  // URL do avatar a ser usada (prioridade: estado global > props)
  // Estado global já tem cache-busting aplicado pelo useAvatarSync
  // NÃO remover o cache-busting aqui, pois ele é necessário para evitar cache do navegador
  const effectiveSrc = globalAvatarUrl || src;  // Prioridade para estado global (já com cache-busting)
  const isLoading = showLoading && (imageLoading || globalLoading);
  const hasError = imageError || !!globalError;
  const shouldShowFallback = !effectiveSrc || isLoading || hasError;

  // Iniciais para fallback
  const getInitials = useCallback(() => {
    // Prioridade: props > estado global > fallback customizado
    if (fallback) return fallback;

    const name = userName || globalInitials || '';
    if (name) return name;

    // Fallback: gerar das informações disponíveis
    const fallbackName = userName || '';
    if (fallbackName) {
      return fallbackName
        .split(' ')
        .map(part => part.charAt(0).toUpperCase())
        .join('')
        .slice(0, 2);
    }

    const fallbackEmail = userEmail || '';
    if (fallbackEmail) {
      return fallbackEmail.charAt(0).toUpperCase();
    }

    return 'U';
  }, [userName, userEmail, globalInitials, fallback]);

  // Estabiliza a URL da imagem usando useMemo baseado na URL base (sem cache-busting)
  // Isso evita recarregamentos infinitos causados por novos timestamps a cada render
  const imageSrc = React.useMemo(() => {
    if (!effectiveSrc || imageError) return null;

    try {
      // Se a URL já tem cache-busting (vem do estado global), usa diretamente
      if (effectiveSrc.includes('?t=')) {
        return effectiveSrc;
      }

      // Se não tem cache-busting, extrai a URL base e usa ela
      // O cache-busting será aplicado pelo estado global quando necessário
      const cleanUrl = effectiveSrc.split('?')[0];
      return cleanUrl;
    } catch (error) {
      console.error('❌ Error processing image URL:', error, effectiveSrc);
      return null;
    }
  }, [effectiveSrc, imageError]);

  // Tratamento de eventos da imagem
  // Usar ref para imageSrc para evitar recriação do callback a cada mudança de timestamp
  const imageSrcRef = React.useRef(imageSrc);
  React.useEffect(() => {
    imageSrcRef.current = imageSrc;
  }, [imageSrc]);

  const handleImageLoad = useCallback(() => {
    console.log('✅ Avatar image loaded successfully:', imageSrcRef.current);
    setImageLoading(false);
    setImageError(false);
    setImageAttempted(true);
    setLoading(false);
    setError(null);
  }, [setLoading, setError]);

  const handleImageError = useCallback((error: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('❌ Avatar image failed to load:', {
      src: imageSrc,
      effectiveSrc,
      error: error.currentTarget?.error?.message || 'Unknown error',
      naturalWidth: error.currentTarget?.naturalWidth,
      naturalHeight: error.currentTarget?.naturalHeight,
      complete: error.currentTarget?.complete
    });
    setImageLoading(false);
    setImageError(true);
    setImageAttempted(true);
    setLoading(false);
    setError('Falha ao carregar avatar');
  }, [setLoading, setError, imageSrc, effectiveSrc]);

  // Força refresh quando prop mudar
  useEffect(() => {
    if (forceRefresh) {
      setImageKey(prev => prev + 1);
      setImageError(false);
      setImageAttempted(false);
    }
  }, [forceRefresh]);

  // Reset estados quando URL BASE muda (sem cache-busting para comparação estável)
  const effectiveSrcBase = React.useMemo(() => {
    return effectiveSrc ? effectiveSrc.split('?')[0] : null;
  }, [effectiveSrc]);

  useEffect(() => {
    if (effectiveSrcBase) {
      setImageLoading(true);
      setImageError(false);
      setImageAttempted(false);
    } else {
      setImageLoading(false);
      setImageError(false);
      setImageAttempted(false);
    }
  }, [effectiveSrcBase]); // Usa URL base para evitar re-triggers por mudanças de cache-busting

  // Usa URL base para key estável, evitando re-renders por mudanças de cache-busting
  // CRÍTICO: Este hook deve estar ANTES de qualquer early return
  const stableKey = React.useMemo(() => {
    const baseUrl = effectiveSrcBase || 'no-avatar';
    return `avatar-${baseUrl}-${imageKey}`;
  }, [effectiveSrcBase, imageKey]);

  // Se não há URL de avatar, mostrar apenas fallback
  // IMPORTANTE: Early return DEPOIS de todos os hooks
  if (!effectiveSrc) {
    return (
      <Avatar className={`${sizeClasses[size]} ${className}`} onClick={onClick}>
        <AvatarFallback className={`${fallbackSizeClasses[size]} bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold border border-white/20`}>
          {getInitials()}
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <Avatar
      key={stableKey} // Key estável baseada na URL base, não no cache-busting
      className={`${sizeClasses[size]} ${className} ${hasError ? 'opacity-90' : ''}`}
      onClick={onClick}
    >
      {imageSrc && (
        <AvatarImage
          src={imageSrc}
          alt={alt}
          className="object-cover"
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      )}

      {/* Fallback sempre visível durante carregamento, erro ou quando não há imagem */}
      <AvatarFallback
        className={`${fallbackSizeClasses[size]} ${
          hasError
            ? 'bg-gradient-to-br from-red-500 to-orange-600 text-white'
            : 'bg-gradient-to-br from-blue-500 to-purple-600 text-white'
        } font-semibold border border-white/20 transition-all duration-200`}
      >
        {hasError ? (
          // Icone de erro quando falha no carregamento
          <span className="text-lg">⚠</span>
        ) : imageLoading && effectiveSrc ? (
          // Indicador de carregamento
          <div className="animate-pulse w-full h-full bg-card/20 rounded-full" />
        ) : (
          // Iniciais
          getInitials()
        )}
      </AvatarFallback>
    </Avatar>
  );
}